import { usePageSEO } from "@/hooks/use-seo";
import { Helmet } from "react-helmet-async";
import PublicHeader from "@/components/layout/PublicHeader";
import Footer from "@/components/layout/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, HelpCircle, Shield, CreditCard, FileText, AlertTriangle, Wrench } from "lucide-react";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Zoocertica?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Zoocertica is a professional wildlife-management platform for accredited facilities and licensed private collectors. It allows you to track animals, manage medical and husbandry data, maintain regulatory compliance, and exchange animals securely through digitally signed transfer certificates."
      }
    },
    {
      "@type": "Question",
      "name": "What is the Collector Plan?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Collector Plan is for responsible reptile, amphibian, and aquatic keepers. It provides professional-grade recordkeeping with features like animal inventory tracking, feeding and growth entries, breeding and lineage tracking, legality checker by state/species, and transfer certificates."
      }
    },
    {
      "@type": "Question",
      "name": "How do animal transfers work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Transfers record the handoff of an animal between two verified Zoocertica users. You initiate a transfer from the animal profile, choose what data to include, and sign electronically. The recipient receives an email, accepts the transfer, and both parties receive a Transfer Certificate PDF with a verification QR code."
      }
    },
    {
      "@type": "Question",
      "name": "How much does the Collector Plan cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Collector Plan pricing starts at $20/month ($200/year) for Starter with 20 animals and 200 entries/month, $30/month ($300/year) for Plus with 50 animals and 500 entries/month, and $50/month ($500/year) for Pro with 150 animals and unlimited entries."
      }
    },
    {
      "@type": "Question",
      "name": "What are the system requirements?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Zoocertica requires a modern browser (Chrome, Firefox, Edge, Safari) and a stable internet connection. For mobile, Android 10+ or iOS 15+ is recommended."
      }
    }
  ]
};

const HelpCenter = () => {
  usePageSEO(
    "Help Center - Zoocertica",
    "Comprehensive help and documentation for Zoocertica's wildlife management platform for collectors, zoos, and sanctuaries.",
    "/help"
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <PublicHeader />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Zoocertica Help Center</h1>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about using Zoocertica for wildlife management
            </p>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <Card className="hover:border-primary transition-colors cursor-pointer">
              <CardHeader>
                <BookOpen className="h-8 w-8 mb-2 text-primary" />
                <CardTitle className="text-lg">Getting Started</CardTitle>
                <CardDescription>Learn the basics</CardDescription>
              </CardHeader>
            </Card>
            <Card className="hover:border-primary transition-colors cursor-pointer">
              <CardHeader>
                <HelpCircle className="h-8 w-8 mb-2 text-primary" />
                <CardTitle className="text-lg">Collector Plan</CardTitle>
                <CardDescription>Features for keepers</CardDescription>
              </CardHeader>
            </Card>
            <Card className="hover:border-primary transition-colors cursor-pointer">
              <CardHeader>
                <FileText className="h-8 w-8 mb-2 text-primary" />
                <CardTitle className="text-lg">Transfers</CardTitle>
                <CardDescription>Exchange animals safely</CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Main Content Accordion */}
          <Accordion type="single" collapsible className="w-full space-y-4">
            {/* Getting Started */}
            <AccordionItem value="getting-started" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <span>1. Getting Started</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Welcome to Zoocertica</h3>
                  <p className="text-muted-foreground mb-4">
                    Zoocertica is a professional wildlife-management platform for accredited facilities and licensed private collectors. It allows you to track animals, manage medical and husbandry data, maintain regulatory compliance, and exchange animals securely through digitally signed transfer certificates.
                  </p>
                  <p className="text-muted-foreground mb-2">Two types of accounts exist:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li><strong>Zoo / Sanctuary Plans</strong> – designed for multi-staff facilities with full inspection, permit, and medical modules.</li>
                    <li><strong>Collector Plans</strong> – for individual reptile, amphibian, and aquatic keepers who need streamlined recordkeeping.</li>
                  </ul>
                  <p className="text-muted-foreground mt-4">
                    If you manage both, you can link them through the Collector Add-On (50% discounted add-on for zoo users).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">System Requirements</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Modern browser (Chrome, Firefox, Edge, Safari)</li>
                    <li>Stable internet connection</li>
                    <li>For mobile: Android 10+ or iOS 15+ recommended</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Logging In</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Visit zoocertica.com/login</li>
                    <li>Enter your email and password</li>
                    <li>If you forget your password, use "Forgot Password" to reset via email</li>
                    <li>Two-factor authentication is available under Account → Security</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Collector Plan */}
            <AccordionItem value="collector-plan" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  <span>2. Collector Plan</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Overview</h3>
                  <p className="text-muted-foreground">
                    The Collector Plan is for responsible reptile, amphibian, and aquatic keepers. It provides professional-grade recordkeeping adapted for small-scale collections.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Key Features</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Animal inventory with species, morph, and identifiers</li>
                    <li>Feeding, growth, and environmental entries</li>
                    <li>Breeding & lineage tracking</li>
                    <li>Legality checker by state/species</li>
                    <li>Transfer certificates for safe, documented exchanges</li>
                    <li>Exportable CSV/PDF reports</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Collector Dashboard</h3>
                  <p className="text-muted-foreground mb-2">Your dashboard summarizes:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li><strong>Animal Count</strong> – total active animals</li>
                    <li><strong>Recent Feedings</strong> – last 7 days</li>
                    <li><strong>Growth Trends</strong> – weight/length over time</li>
                    <li><strong>Environment Alerts</strong> – humidity/temperature extremes</li>
                    <li><strong>Legality Warnings</strong> – species flagged for restrictions</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Adding Animals</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Go to Animals → Add Animal</li>
                    <li>Fill in species, morph, sex, birth/hatch date, and identifiers</li>
                    <li>Optionally attach photos and notes</li>
                    <li>Click Save</li>
                  </ol>
                  <p className="text-muted-foreground mt-3">
                    Each animal receives a unique Zoocertica ID for transfers and record linkage.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Feeding Entries</h3>
                  <p className="text-muted-foreground mb-2">To log a feeding:</p>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Select an animal → Feeding Tab → Add Entry</li>
                    <li>Enter date, prey type, prey size, success/failure, and notes</li>
                    <li>Click Save</li>
                  </ol>
                  <p className="text-muted-foreground mt-3">
                    Charts automatically summarize feeding frequency and success rates.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Growth Entries</h3>
                  <p className="text-muted-foreground mb-2">Track size and condition:</p>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Animal → Growth Tab → Add Entry</li>
                    <li>Enter weight (g) and length (mm)</li>
                    <li>Save; graphs will plot trends automatically</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Environmental Entries</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Enclosures → Select Enclosure → Add Environment Entry</li>
                    <li>Record temperature, humidity, and lighting schedule</li>
                    <li>Data appears in trend charts on the enclosure page</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Breeding & Lineage</h3>
                  <ul className="space-y-3 text-muted-foreground ml-4">
                    <li><strong>Create Pairing:</strong> Breeding → Add Pair → choose male/female animals</li>
                    <li><strong>Record Clutch/Spawn:</strong> Enter dates, fertile / hatched counts</li>
                    <li><strong>Add Offspring:</strong> Link hatchlings to their clutch for lineage tracking</li>
                    <li><strong>Morph / Genetic Traits:</strong> Assign dominant/recessive traits via the genetics picker</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Legality Checker</h3>
                  <p className="text-muted-foreground mb-2">During animal creation or edit:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>The system checks state and federal rules</li>
                    <li>Restricted or permit-required species are highlighted in red</li>
                    <li>Each alert links to citations or permit requirements</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Exports</h3>
                  <p className="text-muted-foreground mb-2">Go to Reports → Exports to download:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Inventory list (CSV or PDF)</li>
                    <li>Breeding summary</li>
                    <li>Transfer history</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Facility Management */}
            <AccordionItem value="facility" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-primary" />
                  <span>3. Facility (Zoo/Sanctuary) Management</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <p className="text-muted-foreground">
                  No changes were made for existing zoo users.
                </p>
                <div>
                  <p className="text-muted-foreground mb-3">Zoo accounts continue using full modules:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Facility Inspections</li>
                    <li>Medical Records & Treatment Logs</li>
                    <li>Permit & License Management</li>
                    <li>Staff Roles and Audit Trails</li>
                  </ul>
                </div>
                <p className="text-muted-foreground">
                  Zoo users can optionally activate the Collector Add-On to unlock the collector dashboards for internal herpetology or aquarium programs.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Transfers & Certificates */}
            <AccordionItem value="transfers" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <span>4. Transfers & Certificates</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Overview</h3>
                  <p className="text-muted-foreground">
                    Transfers record the handoff of an animal between two verified Zoocertica users. Every completed transfer generates a Transfer Certificate (PDF) with a verification QR code.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Initiating a Transfer</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Open the animal profile → Actions → Initiate Transfer</li>
                    <li>Enter recipient's email or username</li>
                    <li>Choose what data to include:
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li>Basic profile (always)</li>
                        <li>Feeding, growth, environmental entries (optional)</li>
                        <li>Medical data (optional; redacted by default)</li>
                      </ul>
                    </li>
                    <li>Confirm and sign electronically</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Recipient Acceptance</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Recipient receives an email invitation</li>
                    <li>After logging in, they can review and accept</li>
                    <li>Certificate is generated for both parties</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Certificate Contents</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Animal ID, species, sex, DOB</li>
                    <li>From/To owners and timestamps</li>
                    <li>Data scope summary</li>
                    <li>Verification hash and QR link</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Audit Trail</h3>
                  <p className="text-muted-foreground">
                    All transfers are logged in Transfers → History with statuses (Pending, Accepted, Cancelled).
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Billing & Plans */}
            <AccordionItem value="billing" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <span>5. Billing & Plans</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Collector Plan Pricing</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">Plan</th>
                          <th className="text-left py-3 px-4">Monthly</th>
                          <th className="text-left py-3 px-4">Annual</th>
                          <th className="text-left py-3 px-4">Features</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-3 px-4 font-medium">Starter</td>
                          <td className="py-3 px-4">$20</td>
                          <td className="py-3 px-4">$200</td>
                          <td className="py-3 px-4">20 animals · 200 entries/month</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-3 px-4 font-medium">Plus</td>
                          <td className="py-3 px-4">$50</td>
                          <td className="py-3 px-4">$500</td>
                          <td className="py-3 px-4">200 animals · unlimited entries</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 font-medium">Pro</td>
                          <td className="py-3 px-4">$99</td>
                          <td className="py-3 px-4">$990</td>
                          <td className="py-3 px-4">Unlimited everything</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-muted-foreground mt-3">
                    Billing handled via Stripe; you can manage subscriptions at Account → Billing.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Zoo Add-On Pricing</h3>
                  <p className="text-muted-foreground mb-3">
                    Existing Zoo/Sanctuary subscribers may add Collector functionality at 50% off.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">Add-On Tier</th>
                          <th className="text-left py-3 px-4">Monthly</th>
                          <th className="text-left py-3 px-4">Annual</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-3 px-4 font-medium">Starter Add-On</td>
                          <td className="py-3 px-4">$10</td>
                          <td className="py-3 px-4">$100</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-3 px-4 font-medium">Plus Add-On</td>
                          <td className="py-3 px-4">$25</td>
                          <td className="py-3 px-4">$250</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 font-medium">Pro Add-On</td>
                          <td className="py-3 px-4">$49</td>
                          <td className="py-3 px-4">$490</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-muted-foreground mt-3">
                    Canceling your Zoo plan automatically ends the Collector add-on.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Upgrading or Downgrading</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Upgrades take effect immediately; price prorated</li>
                    <li>Downgrades apply at next billing cycle</li>
                    <li>Annual plans renew automatically unless canceled</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Refund Policy</h3>
                  <p className="text-muted-foreground">
                    Zoocertica offers refunds within 14 days of first subscription if no transfers or exports were generated. Contact support for manual review.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Legal & Compliance */}
            <AccordionItem value="legal" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  <span>6. Legal & Compliance</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Terms of Service Highlights</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Users must comply with local wildlife laws</li>
                    <li>Data you store remains yours; Zoocertica provides secure hosting</li>
                    <li>Transfers and certificates are documentation aids, not legal permits</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Privacy</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>All personal data is encrypted at rest and in transit</li>
                    <li>Zoocertica does not sell or share customer data</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Troubleshooting & Support */}
            <AccordionItem value="troubleshooting" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                <div className="flex items-center gap-3">
                  <Wrench className="h-5 w-5 text-primary" />
                  <span>7. Troubleshooting & Support</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-6 pt-4 text-base">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Common Issues</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">Issue</th>
                          <th className="text-left py-3 px-4">Resolution</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-3 px-4">Collector modules missing</td>
                          <td className="py-3 px-4">Ensure your plan or add-on is active. Refresh after Stripe confirmation.</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-3 px-4">Transfer certificate not generating</td>
                          <td className="py-3 px-4">Check both parties accepted. Pending transfers don't create PDFs.</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-3 px-4">Can't add more animals</td>
                          <td className="py-3 px-4">You may have reached plan limit; upgrade under Billing.</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4">Incorrect legality alert</td>
                          <td className="py-3 px-4">Report rule discrepancy via Support → Legality Feedback.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Contact Support</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>Email: support@zoocertica.com</li>
                    <li>Hours: Mon–Fri, 9 AM–6 PM EST</li>
                    <li>Response time: under 24 hours for paid plans</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Contact CTA */}
          <Card className="mt-12 bg-primary/5 border-primary/20">
            <CardHeader className="text-center">
              <CardTitle>Still Need Help?</CardTitle>
              <CardDescription>
                Our support team is here to assist you with any questions
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <a 
                href="/contact" 
                className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground h-10 px-8 font-medium hover:bg-primary/90 transition-colors"
              >
                Contact Support
              </a>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpCenter;
